import FormField from '@bonniernews/dn-design-system-web/assets/form-field/form-field.tsx'
import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export interface CheckboxProps extends SharedProps {
  error?: boolean
  errorMessage?: string
  label?: string
  stripLabel?: boolean
  checked?: boolean
  required?: boolean
  disabled?: boolean
  name?: string
  validation?: string
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/checkbox](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/checkbox)
 * - Storybook: [Checkbox](https://designsystem.dn.se/?path=/docs/basic-form-checkbox--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/checkbox/checkbox.scss`
 */
export const Checkbox = ({
  error,
  errorMessage,
  label,
  stripLabel,
  name = 'ds-checkbox',
  validation,
  checked,
  required,
  disabled,
  forcePx,
  classNames,
  attributes,
}: CheckboxProps) => {
  const componentClassName = 'ds-checkbox'
  const wrapperClasses = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])

  return (
    <FormField error={error} errorMessage={label && errorMessage} wrapperClasses={wrapperClasses}>
      <input
        type='checkbox'
        id={name}
        name={name}
        className={`${componentClassName}__input js-field-error`}
        data-validation={validation}
        {...attributes}
        defaultChecked={checked}
        disabled={disabled}
        required={required}
      />

      {stripLabel ? (
        <div className={`${componentClassName}__inner`}>
          <CheckboxIcons componentClassName={componentClassName} />
        </div>
      ) : (
        <label className={`${componentClassName}__inner`} htmlFor={name}>
          <CheckboxIcons componentClassName={componentClassName} />
          {label && <span className={`${componentClassName}__text`}>{label}</span>}
        </label>
      )}
    </FormField>
  )
}

const CheckboxIcons = ({ componentClassName }: { componentClassName: string }) => {
  return (
    <span className={`${componentClassName}__icon`}>
      <IconUse iconName='check_box_outline_blank' />
      <IconUse iconName='check_box-filled' />
    </span>
  )
}

export default Checkbox
